import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import useTitle from 'utils/customHook/useTitle'
import i18n from 'simple-react-i18n'
import { IndicatorPanel } from './IndicatorsPanels'
import useActions from 'utils/customHook/useActions'
import { Card, CardContent, Grid, Stack, Typography } from '@mui/material'
import StatusPanel from './StatusPanel'
import { getYear } from 'utils/DateUtil'
import UpdateEntityPanel from '../rpqs/UpdateEntityPanel'
import RPQSBreadCrumb from '../RPQSBreadCrumb'
import RpqsAction from 'rpqs/actions/RpqsAction'
import CityAction from 'referencial/components/city/actions/CityAction'
import { computeRef001 } from 'rpqs/utils/RPQSFormula'
import ProgressCard from '../../../components/card/ProgressCard'
import { isRpqsLoaded } from '../../utils/RPQSUtils'
import RpqsNotesPanel from '../dashboard/RpqsNotesPanel'
import RpqsLastEventPanel from '../dashboard/RpqsLastEventPanel'
import RpqsFilesPanel from '../dashboard/RpqsFilesPanel'

const ManagementEntityDasboardApp = () => {
    const {
        rpqs,
        collectivities,
        managementUnitProps,
        cities,
        citiesPopulations,
    } = useSelector(store => ({
        rpqs: store.RpqsReducer.rpqs,
        collectivities: store.RpqsReducer.collectivities,
        managementUnitProps: store.RpqsReducer.managementUnit,
        cities: store.CityReducer.cities,
        citiesPopulations: store.RpqsReducer.citiesPopulations,
    }), shallowEqual)

    const { annee, idCollectivite, ideg } = useParams()
    const dispatch = useDispatch()

    const [indicatorsResult, setIndicatorsResult] = useState({})

    const refreshIndicators = useCallback(
        () => dispatch(RpqsAction.fetchIndicators(annee, idCollectivite, ideg)).then(res => res && setIndicatorsResult(res)),
        [dispatch, annee, idCollectivite, ideg],
    )

    useEffect(() => {
        dispatch(RpqsAction.fetchAllCollectivities())
        dispatch(CityAction.fetchCities())
        refreshIndicators()
    }, [annee, idCollectivite, ideg])

    const isLoaded = isRpqsLoaded(rpqs, annee, idCollectivite) && managementUnitProps?.id === parseInt(ideg)

    const [managementUnit, setManagementUnit] = useState({})
    const [readMode, setReadMode] = useState(true)
    const [notes, setNotes] = useState('')

    useEffect(() => {
        setNotes(rpqs.notes ?? '')
    }, [rpqs.notes])

    useEffect(() => {
        if (isLoaded) {
            setManagementUnit(managementUnitProps)
        }
    }, [managementUnitProps, isLoaded])

    // REF.001 : populations INSEE des communes de l'entité de gestion (pour le moteur de formules).
    useEffect(() => {
        const cityIds = managementUnitProps.link_cities?.map(c => c.cityCode) ?? []
        if (cityIds.length) dispatch(CityAction.getCitiesPopulations(cityIds))
    }, [managementUnitProps.link_cities, dispatch])

    const refValues = useMemo(() => ({
        'REF.001': computeRef001({ linkCities: managementUnitProps.link_cities, cities, citiesPopulations, year: rpqs.year }),
    }), [managementUnitProps.link_cities, cities, citiesPopulations, rpqs.year])

    const collectivity = collectivities.find(c => c.id === rpqs.collectivityId)

    const year = rpqs.year || `${i18n.year} ${i18n.unknown}`
    const managementEntityPropsName = managementUnitProps.name || `${i18n.managementEntity} ${i18n.unknown}`

    useTitle(() => [{
        title: i18n.rpqs,
        href: 'rpqs',
    }, {
        title: year,
        href: 'rpqs',
    }, {
        title: rpqs.name,
        href: `rpqs/${annee}/collectivite/${idCollectivite}`,
    }, {
        title: managementEntityPropsName,
        href: `rpqs/${annee}/collectivite/${idCollectivite}/eg/${ideg}/dashboard`,
    }], [collectivity, managementUnitProps, annee, idCollectivite, ideg])

    useActions(() => {
        if (readMode) {
            return {
                import: () => {},
                delete: () => {},
                export: () => {},
                edit: () => setReadMode(false),
            }
        }
        return {
            cancel: () => {
                setManagementUnit(managementUnitProps)
                setNotes(rpqs.notes ?? '')
                setReadMode(true)
            },
            save: () => {
                dispatch(RpqsAction.updateRpqs({ ...rpqs, notes })).then(json => {
                    if (json?.update) setReadMode(true)
                })
            },
        }
    }, [readMode, rpqs, notes, managementUnitProps])

    if (!isLoaded) {
        return <ProgressCard indeterminate round />
    }

    return (
        <Grid container columnSpacing={1} sx={{ padding: 1 }}>
            <Grid size={9}>
                <Stack spacing={1}>
                    <Card>
                        <CardContent>
                            <Stack spacing={1}>
                                <Stack>
                                    <Typography variant='title'>{i18n.rpqs} {year}</Typography>
                                    <Typography variant='title'>{rpqs.name}</Typography>
                                    <Typography variant='title'>{managementEntityPropsName}</Typography>
                                </Stack>
                                <RPQSBreadCrumb />
                            </Stack>
                        </CardContent>
                    </Card>
                    <IndicatorPanel
                        rpqs={rpqs}
                        egId={ideg}
                        indicators={indicatorsResult.indicators ?? []}
                        progress={indicatorsResult}
                        refValues={refValues}
                        onSaved={refreshIndicators}
                    />
                </Stack>
            </Grid>
            <Grid size={3}>
                <Stack spacing={1}>
                    <StatusPanel
                        status={managementUnit?.[getYear(managementUnit?.endDate)]?.status}
                    />
                    <UpdateEntityPanel
                        updateDate={managementUnit?.updateDate}
                        updateLogin={managementUnit?.updateLogin}
                        entityName={managementUnit?.name}
                    />
                    <RpqsNotesPanel notes={notes} onChange={setNotes} readMode={readMode} />
                    <RpqsLastEventPanel year={annee} collectivityId={idCollectivite} egId={ideg} />
                    <RpqsFilesPanel year={annee} collectivityId={idCollectivite} egId={ideg} />
                </Stack>
            </Grid>
        </Grid>
    )
}

export default ManagementEntityDasboardApp
